/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../platform/Platform';
import { ComponentManagerFn } from '../../../registry/Component.type';
import { ItemType } from '../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../registry/Components';
import { defaultValue } from './PopupActions';

const PopupActionsManager: ComponentManagerFn<'PopupActions'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'PopupActions'>(platform, 'PopupActions'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (['ActionButton', 'Button', 'Toolbar'] as ItemType[]).includes(childType),
    props: {
      bgColor: {
        type: 'BGColor',
        label: 'Background Color',
        defaultValue: '',
        allowCustomColor: true,
      },
    },
    defaultChildren: ['Button'],
    category: 'Containers',
    name: 'Popup Actions',
    canHaveChildren: () => true,
    layout: 'auto',
    icon: { iconName: 'play' },
    description: `**Overview**<br/>
The Popup Actions component serves as the dedicated footer container for a Popup window. It is the standardized location for primary user interactions—such as "Save," "Submit," or "Cancel" buttons—ensuring that call-to-action elements are always easily accessible at the bottom of the dialog.
By default, this container automatically centers all items placed inside it, providing a balanced and professional layout without the need for manual alignment configuration.

**1. Container Layout & Behavior**<br/>
This component is purpose-built to sit at the bottom of a Popup and enforce consistent styling across your application.
* **Placement:** The component is designed to be a direct child of a Popup. It will automatically anchor itself to the bottom of the window, separate from the main content body.
* **Alignment:** All child components (buttons, links, toolbars) are aligned to the center of the footer area. This convention helps guide user focus to the primary actions.

**2. Visual Customization**<br/>
While the layout is rigid to ensure consistency, you can adjust the visual presentation to match your design system.
* **Background Color:** Sets the fill color of the footer area. This is often used to visually distinguish the action area from the main content body (e.g., giving the footer a light gray background while the body remains white).

**3. Content Guidelines**<br/>
To maintain semantic correctness and prevent layout issues, this container restricts the types of components it can host.
* **Allowed Children:** Button (Default), Action Button, Toolbar.
* **Restriction:** Do not attempt to place heavy content elements (like Grids or Forms) inside this container; it is strictly intended for buttons and simple interaction triggers.`,
  };
};

export default PopupActionsManager;
